import React, { useEffect, useState, useCallback } from 'react';
import {
  useApolloClient,
  useQuery as useApolloNativeQuery,
  QueryHookOptions,
  QueryResult,
  NetworkStatus,
  OperationVariables,
} from '@apollo/client';
import type { ApolloClient, ApolloError, QueryOptions } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Event from 'geval/event';

import createContextHelpers from 'shared/helpers/context';

type QueryDataResult<Q, V extends OperationVariables> = QueryResult<Q, V>['data'];

type CachedQueryResult<Q, V extends OperationVariables> = {
  loading: boolean;
  data?: QueryDataResult<Q, V>;
  refetch: () => Promise<CachedQueryResult<Q, V>>;
  error?: ApolloError;
  variables?: V;
  networkStatus: NetworkStatus;
};

type CacheEntry<Q, V extends OperationVariables> = {
  listen: () => () => void;
  hasRun: boolean;
  result: CachedQueryResult<Q, V>;
};

type CacheEntryMap = Record<string, CacheEntry<unknown, OperationVariables>>;

export const queryStore = { cache: new WeakMap<any, CacheEntryMap>() };

const QueryStoreContext = React.createContext(queryStore);
export const { QueryStoreProvider, useQueryStore, withQueryStore } = createContextHelpers(
  QueryStoreContext,
  `queryStore`
);

function getCircularReplacer(): (key: string, value: any) => any {
  const seen = new WeakSet();
  return (_key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      const obj = value as object;
      if (seen.has(obj)) {
        return '[Circular]';
      }
      seen.add(obj);
    }
    return value;
  };
}

function createCacheEntry<Q, V extends OperationVariables>(
  apolloClient: ApolloClient<object>,
  operation: DocumentNode,
  options?: QueryHookOptions<Q, V>
): CacheEntry<Q, V> {
  const event = Event();
  const entry: CacheEntry<Q, V> = {
    listen: event.listen,
    hasRun: false,
    result: {
      loading: false,
      variables: options?.variables,
      refetch,
      networkStatus: NetworkStatus.loading,
    },
  };

  async function refetch(): Promise<CachedQueryResult<Q, V>> {
    entry.result.loading = true;
    if (entry.hasRun) {
      entry.result.networkStatus = NetworkStatus.refetch;
    } else {
      entry.result.networkStatus = NetworkStatus.loading;
    }
    entry.hasRun = true;

    try {
      const result = await apolloClient.query({
        query: operation,
        ...options,
      } as QueryOptions<V, Q>);
      entry.result.data = result.data;
      entry.result.error = result.error;
      entry.result.networkStatus = NetworkStatus.ready;
    } catch (error) {
      entry.result.networkStatus = NetworkStatus.error;
      entry.result.error = error;
    }
    entry.result.loading = false;

    // must be async, apolloClient.query isn't always async so we need to dezalgo right here
    setTimeout(() => event.broadcast(entry));
    return entry.result;
  }

  return entry;
}

// One-shot WeakMap-cached implementation — only re-renders on its own refetch broadcast,
// not on arbitrary Apollo cache writes. The flag ecomm.use-custom-usequery-hook.experiment
// selects between this and Apollo's native ObservableQuery at runtime.
function useCustomQueryImpl<Q, V extends OperationVariables>(
  operation: DocumentNode,
  options?: QueryHookOptions<Q, V>
): CachedQueryResult<Q, V> {
  const [, setCount] = useState(0);
  const apolloClient = useApolloClient();
  const store = useQueryStore();

  const variablesString = JSON.stringify(options?.variables, getCircularReplacer());

  const rerender = useCallback(() => setCount((prevCount) => prevCount + 1), []);

  let cacheMap = store.cache.get(operation);
  if (!cacheMap) {
    cacheMap = {};
    store.cache.set(operation, cacheMap);
  }
  let cacheEntry = cacheMap[variablesString];

  if (!cacheEntry) {
    cacheEntry = createCacheEntry(apolloClient, operation, options);
    cacheMap[variablesString] = cacheEntry;
  }

  if (!cacheEntry.hasRun && !options?.skip) {
    // trigger initial fetch
    cacheEntry.result.refetch();
  }

  // listen on changes
  useEffect(() => {
    if (!options?.skip) {
      const unlisten = cacheEntry.listen(rerender);
      // Guard against race condition: refetch() is called during render but the
      // broadcast (in a setTimeout) can fire before this effect registers the
      // listener, leaving the component stuck on the stale loading snapshot.
      if (cacheEntry.hasRun && !cacheEntry.result.loading) {
        rerender();
      }
      return unlisten;
    }
    return undefined;
  }, [options?.skip, cacheEntry, rerender]);

  // Apollo v3 readQuery returns null for cache misses.
  // The try/catch remains as a safety net for edge cases where readQuery may throw.
  try {
    const data = apolloClient.readQuery({
      query: operation,
      variables: options?.variables,
    });

    if (data === null) {
      return cacheEntry.result;
    }

    return { ...cacheEntry.result, data };
  } catch (error) {
    if (error.name === 'Invariant Violation' && error.message.startsWith("Can't find field ")) {
      return cacheEntry.result;
    }

    return { ...cacheEntry.result, error };
  }
}

export function useQuery<Q, V extends OperationVariables>(
  operation: DocumentNode,
  options?: QueryHookOptions<Q, V>
): CachedQueryResult<Q, V> {
  const flags = useFlags();
  // true (default) → custom one-shot hook — fixes ObservableQuery re-render cascade
  // false          → Apollo native ObservableQuery — kill-switch if issues arise
  const useCustom = flags['ecomm.use-custom-usequery-hook.experiment'] !== false;

  // Both hooks are called unconditionally to satisfy the Rules of Hooks.
  // Each path receives skip:true when it is not the active implementation,
  // ensuring only one of them fires network requests at a time.
  const customResult = useCustomQueryImpl<Q, V>(operation, {
    ...options,
    skip: !useCustom || !!options?.skip,
  });
  const nativeResult = useApolloNativeQuery<Q, V>(operation, {
    ...options,
    skip: useCustom || !!options?.skip,
  });

  if (useCustom) {
    return customResult;
  }

  // Normalize Apollo's QueryResult to CachedQueryResult for a consistent interface.
  return {
    data: nativeResult.data,
    loading: nativeResult.loading,
    error: nativeResult.error,
    refetch: async () => {
      const res = await nativeResult.refetch();
      return {
        data: res.data,
        loading: false,
        error: undefined,
        refetch: async () => ({} as CachedQueryResult<Q, V>),
        variables: options?.variables,
        networkStatus: NetworkStatus.ready,
      };
    },
    variables: nativeResult.variables,
    networkStatus: nativeResult.networkStatus,
  };
}
